import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import SectionRenderer from '@/components/sections/SectionRenderer';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function CMSPage({ pageSlug }) {
  const params = useParams();
  const slug = pageSlug || params.slug || params.serviceId || 'home';
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Contact form state
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: 'General Inquiry', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!slug || slug === 'undefined') return;
    setLoading(true); setError(null);
    api.get(`/cms/pages/${slug}`).then(r => setPage(r.data)).catch(() => setError('Page not found')).finally(() => setLoading(false));
  }, [slug]);

  useSeoMeta(page);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) { toast.error('Please fill in all required fields'); return; }
    setSending(true);
    try {
      await api.post('/contact', contactForm);
      setSent(true); toast.success('Message sent!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send message'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-muted-foreground text-sm">Loading...</div></div>;
  if (error || !page) return <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 text-center"><h1 className="font-display text-2xl font-medium text-ink mb-2">Page Not Found</h1><p className="text-sm text-muted-foreground">The page you're looking for doesn't exist.</p></div>;

  return (
    <div data-testid={`cms-page-${slug}`}>
      <SectionRenderer sections={page?.sections || []} />

      {/* Contact form — only on the contact page */}
      {slug === 'contact' && (
        <section className="py-12 sm:py-16">
          <div className="max-w-xl mx-auto px-4 sm:px-6">
            {sent ? (
              <Card className="bg-white border-stone/50">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="h-10 w-10 mx-auto mb-3" style={{ color: '#0E6B5D' }} />
                  <h2 className="font-display text-xl font-medium text-ink mb-2">Message Sent</h2>
                  <p className="text-sm text-muted-foreground">Thank you for reaching out. We'll get back to you at {contactForm.email}.</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border-stone/50">
                <CardContent className="p-6">
                  <h2 className="font-display text-lg font-medium text-ink mb-4">Send Us a Message</h2>
                  <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Name *</Label><Input value={contactForm.name} onChange={e => setContactForm({...contactForm, name: e.target.value})} className="mt-1" placeholder="Your name" data-testid="contact-name" /></div>
                      <div><Label className="text-xs">Email *</Label><Input type="email" value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} className="mt-1" placeholder="you@example.com" data-testid="contact-email" /></div>
                    </div>
                    <div><Label className="text-xs">Subject</Label>
                      <Select value={contactForm.subject} onValueChange={v => setContactForm({...contactForm, subject: v})}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                          <SelectItem value="Order Question">Order Question</SelectItem>
                          <SelectItem value="Authentication Question">Authentication Question</SelectItem>
                          <SelectItem value="Appraisal Question">Appraisal Question</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Message *</Label>
                      <textarea value={contactForm.message} onChange={e => setContactForm({...contactForm, message: e.target.value})} className="w-full mt-1 p-3 rounded-lg border border-stone text-sm min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-ring/30" placeholder="How can we help?" data-testid="contact-message" />
                    </div>
                    <Button type="submit" disabled={sending} className="gap-2 rounded-[12px] w-full sm:w-auto" data-testid="contact-submit">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                    <p className="text-[10px] text-muted-foreground">Messages are sent to support@thefashiondetective.com. No auto-reply.</p>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
